// Chart export: rasterise a Recharts chart, then hand it back as a Blob.
//
// --- Why not html2canvas (#83) -----------------------------------------------
//
// The first attempt at this issue kept html2canvas and shrank the canvas by a
// pixel budget. That was the wrong lever and it did not fix the report: Chrome
// still showed "Page unresponsive" and Safari still failed.
//
// html2canvas's cost is not the canvas area, it is the DOM. It clones the whole
// subtree, then calls getComputedStyle on every cloned node to resolve the
// cascade, then paints. That work scales with node count, so a Recharts chart —
// hundreds of ticks, gridlines and label nodes — is expensive no matter how few
// pixels come out the other end. Halving the scale halved the pixels and left the
// blocking work almost untouched.
//
// It is also the wrong tool for the job. Recharts already renders SVG, which is
// a complete, self-contained description of the picture. html2canvas exists to
// solve a much harder problem — rasterise arbitrary styled HTML — and has long
// been unreliable with SVG subtrees.
//
// So this module renders the chart's own <svg> with canvg instead. canvg walks
// the SVG tree, which is already presentational: no clone, no cascade
// resolution, no getComputedStyle storm. The only computed styles read are the
// font properties on text nodes, of which there are tens rather than hundreds.
//
// Two further changes matter as much as the renderer, both on the download path:
//
//   - The old code built a `data:` URL with canvas.toDataURL(). That
//     base64-encodes the whole image synchronously into a JS string — a second
//     copy of the image, on the main thread — and iOS Safari refuses very large
//     data: URLs outright. canvas.toBlob() encodes asynchronously and yields a
//     Blob, and the download uses an object URL.
//   - jsPDF's pdf.save() and a bare <a download> are unreliable on iOS Safari.
//     downloadBlob() below falls back to opening the object URL when the
//     download attribute is unsupported, so the file reaches the share sheet
//     rather than silently doing nothing.
//
// Kept in one module because both chart exports need it and any future one will
// too. canvg is imported dynamically so it stays out of the main bundle.

// --- Pixel budget -------------------------------------------------------------
//
// Still needed, just no longer the whole fix. Safari refuses canvases beyond
// 2^24 px of area regardless of how the pixels are produced, so the output size
// has to be bounded whichever renderer draws it.

// 6 M px leaves a wide margin under Safari's ceiling and is still far more
// detail than A4 can show.
export const MAX_CANVAS_AREA = 6_000_000;

// A4 portrait content width is 190 mm ≈ 7.48 in, so 1800 px lands near 240 dpi.
export const PDF_TARGET_WIDTH = 1800;

// Upscaling past 3× stops being visible in print; never dropping below 1× avoids
// resampling a chart that is already wide enough, which thins out gridlines.
export const PDF_MAX_SCALE = 3;
export const PDF_MIN_SCALE = 1;

// The JPEG path is a screen-resolution download, not a print.
export const JPEG_SCALE = 1.5;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

// Reduces `requestedScale` if it would push the canvas past `maxArea`. Returns
// the requested scale untouched when it already fits, and may return a value
// below 1 for a chart that is enormous before any upscaling — a downscaled
// export still beats a refused canvas.
export function fitScaleToArea(width, height, requestedScale, maxArea = MAX_CANVAS_AREA) {
  // A detached or zero-size element gives no budget to work with. Hand back the
  // requested scale rather than inventing a number from a division by zero.
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0)
    return requestedScale;
  if (!Number.isFinite(requestedScale) || requestedScale <= 0) return requestedScale;

  const area = width * height * requestedScale * requestedScale;
  if (area <= maxArea) return requestedScale;
  return Math.sqrt(maxArea / (width * height));
}

// Scale for the PDF path: aim for PDF_TARGET_WIDTH of output, clamped to a sane
// multiplier range, then held to the area budget.
export function pdfCaptureScale(width, height, {
  targetWidth = PDF_TARGET_WIDTH,
  minScale = PDF_MIN_SCALE,
  maxScale = PDF_MAX_SCALE,
  maxArea = MAX_CANVAS_AREA,
} = {}) {
  if (!Number.isFinite(width) || width <= 0) return minScale;
  const wanted = clamp(targetWidth / width, minScale, maxScale);
  return fitScaleToArea(width, height, wanted, maxArea);
}

// Scale for the JPEG path: the original fixed 1.5×, still subject to the area
// budget so a very wide chart cannot reach a canvas Safari will refuse.
export function jpegCaptureScale(width, height, {
  scale = JPEG_SCALE,
  maxArea = MAX_CANVAS_AREA,
} = {}) {
  return fitScaleToArea(width, height, scale, maxArea);
}

// --- SVG preparation ----------------------------------------------------------

// Font and colour properties that reach SVG text through the CSS cascade rather
// than through an attribute. Serialising the SVG on its own leaves the stylesheet
// behind, so these are copied onto the clone or canvg renders every label in a
// default face at the wrong size.
const INHERITED_TEXT_PROPS = [
  "font-family",
  "font-size",
  "font-weight",
  "font-style",
  "fill",
  "text-anchor",
  "letter-spacing",
];

// Height reserved under the plot for the legend, in CSS pixels before scaling.
export const LEGEND_BAND_HEIGHT = 26;

// Copies the computed text properties from the live SVG onto the detached clone.
//
// The two node lists are matched by position, which is safe because the clone is
// structural. If they ever disagree, styling by index would attach the wrong font
// to the wrong label, so it bails and lets the clone render in a fallback face.
export function inlineTextStyles(sourceSvg, clonedSvg, getComputedStyleFn) {
  const compute =
    getComputedStyleFn ??
    (typeof window !== "undefined" ? window.getComputedStyle.bind(window) : null);
  if (!compute) return 0;

  const source = sourceSvg.querySelectorAll("text, tspan");
  const clone = clonedSvg.querySelectorAll("text, tspan");
  if (source.length !== clone.length) return 0;

  for (let i = 0; i < source.length; i++) {
    const computed = compute(source[i]);
    if (!computed) continue;
    for (const prop of INHERITED_TEXT_PROPS) {
      const value = computed.getPropertyValue(prop);
      if (value) clone[i].style.setProperty(prop, value);
    }
  }
  return source.length;
}

// Below this, an SVG is not a chart. Recharts' legend icons are 14 px squares, so
// anything near that size is a glyph, and rasterising one produces the blurry
// nonsense the first canvg attempt actually shipped.
export const MIN_CHART_DIMENSION = 60;

// Intrinsic size of an SVG, preferring the laid-out box and falling back to the
// attributes so this also works on a detached node.
export function svgSize(svg) {
  const rect = typeof svg.getBoundingClientRect === "function" ? svg.getBoundingClientRect() : null;
  const width = Math.round(rect?.width || svg.clientWidth || parseFloat(svg.getAttribute("width")) || 0);
  const height = Math.round(rect?.height || svg.clientHeight || parseFloat(svg.getAttribute("height")) || 0);
  return { width, height };
}

// Finds the chart's SVG inside a `.recharts-wrapper`, or accepts an SVG directly.
//
// Deliberately not `querySelector("svg")`. Recharts renders
// `.recharts-legend-wrapper` as the *first* child of the wrapper, and every legend
// item carries its own 14x14 `svg.recharts-surface` icon — so the first SVG in
// document order is a legend glyph, not the chart. Capturing that is what produced
// a report containing one enormous blurry icon.
//
// html2canvas never hit this because it rasterised the whole div and never had to
// choose. Two independent guards, because choosing wrong fails silently:
//   - skip anything inside the legend wrapper
//   - of what remains take the largest, so a Recharts class rename cannot
//     resurrect the bug by making the first guard miss
export function findChartSvg(element) {
  if (!element) return null;
  if (element.tagName && element.tagName.toLowerCase() === "svg") return element;
  if (typeof element.querySelectorAll !== "function") return null;

  const candidates = Array.from(element.querySelectorAll("svg")).filter(
    (svg) => !(typeof svg.closest === "function" && svg.closest(".recharts-legend-wrapper"))
  );
  if (!candidates.length) return null;

  return candidates.reduce((best, svg) => {
    const a = svgSize(svg);
    const b = svgSize(best);
    return a.width * a.height > b.width * b.height ? svg : best;
  });
}

// Detaches a styled copy of the chart SVG and serialises it.
//
// Explicit width/height on the root matter: Safari will not draw an SVG that
// carries only a viewBox, and canvg needs an intrinsic size to lay out against.
export function serializeChartSvg(element, { serializer, getComputedStyleFn } = {}) {
  const svg = findChartSvg(element);
  if (!svg) return null;

  const { width, height } = svgSize(svg);
  if (!width || !height) return null;
  // Refuse a glyph rather than upscaling it into a full-page smear. Failing here
  // surfaces as an export error, which is recoverable; a plausible-looking PDF
  // holding the wrong picture is not.
  if (width < MIN_CHART_DIMENSION || height < MIN_CHART_DIMENSION) return null;

  const clone = svg.cloneNode(true);
  clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
  clone.setAttribute("width", String(width));
  clone.setAttribute("height", String(height));
  if (!clone.getAttribute("viewBox")) {
    clone.setAttribute("viewBox", `0 0 ${width} ${height}`);
  }

  inlineTextStyles(svg, clone, getComputedStyleFn);

  const serialize = serializer ?? new XMLSerializer();
  return { svg: serialize.serializeToString(clone), width, height };
}

// --- Rendering ----------------------------------------------------------------

// Draws a legend row onto the canvas.
//
// Recharts renders its <Legend> as a sibling *div*, not inside the SVG, so
// capturing the SVG alone drops it — the drift chart has three named series and
// losing the key makes the export unreadable. Drawing it here keeps the export
// self-describing, and is more legible than rasterising the DOM legend was.
export function drawLegend(ctx, entries, { width, y, scale = 1 }) {
  if (!entries?.length) return;
  const fontSize = 12 * scale;
  const swatch = 10 * scale;
  const gap = 6 * scale;
  const itemGap = 20 * scale;

  ctx.save();
  ctx.font = `${fontSize}px system-ui, -apple-system, "Segoe UI", sans-serif`;
  ctx.textBaseline = "middle";

  const widths = entries.map((e) => swatch + gap + ctx.measureText(e.label).width);
  const total = widths.reduce((s, w) => s + w, 0) + itemGap * (entries.length - 1);
  let x = Math.max(0, (width - total) / 2);
  const centre = y + (LEGEND_BAND_HEIGHT * scale) / 2;

  entries.forEach((entry, i) => {
    ctx.fillStyle = entry.colour ?? "#6b7280";
    ctx.fillRect(x, centre - swatch / 2, swatch, swatch);
    ctx.fillStyle = "#374151";
    ctx.fillText(entry.label, x + swatch + gap, centre);
    x += widths[i] + itemGap;
  });
  ctx.restore();
}

// Rasterises a chart to a canvas.
//
// `mode` selects the budget: "pdf" upscales toward print resolution, "jpeg"
// keeps the screen-resolution 1.5×. `legend` is drawn under the plot; pass the
// series the chart is actually showing.
export async function captureChart(element, mode = "pdf", { legend = [] } = {}) {
  const prepared = serializeChartSvg(element);
  if (!prepared) throw new Error("Chart SVG not found");
  const { svg, width, height } = prepared;

  const scale = mode === "jpeg"
    ? jpegCaptureScale(width, height)
    : pdfCaptureScale(width, height);

  const bandHeight = legend.length ? LEGEND_BAND_HEIGHT : 0;
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(width * scale);
  canvas.height = Math.round((height + bandHeight) * scale);

  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  const { Canvg } = await import("canvg");
  const renderer = Canvg.fromString(ctx, svg, {
    // One-shot render. Leaving animation on installs a requestAnimationFrame
    // loop that never stops, and mouse handling attaches listeners to a canvas
    // nobody will ever click.
    ignoreMouse: true,
    ignoreAnimation: true,
    // The white background and the legend band are already painted.
    ignoreClear: true,
    // Draw at the budgeted size rather than the SVG's intrinsic size.
    ignoreDimensions: true,
    scaleWidth: Math.round(width * scale),
    scaleHeight: Math.round(height * scale),
  });
  await renderer.render();
  // render() resolves after one pass; stop() makes it explicit that nothing is
  // left scheduled against this context.
  if (typeof renderer.stop === "function") renderer.stop();

  if (legend.length) {
    drawLegend(ctx, legend, { width: canvas.width, y: height * scale, scale });
  }

  return canvas;
}

// --- Output -------------------------------------------------------------------

// Promise wrapper around canvas.toBlob, which encodes off the main thread. Falls
// back to toDataURL only when toBlob is missing, since that synchronous path is
// part of what this change exists to avoid.
export function canvasToBlob(canvas, type = "image/jpeg", quality = 0.95) {
  return new Promise((resolve, reject) => {
    if (typeof canvas.toBlob === "function") {
      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new Error("Canvas encoding failed"))),
        type,
        quality
      );
      return;
    }
    try {
      const parts = canvas.toDataURL(type, quality).split(",");
      const binary = atob(parts[1]);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
      resolve(new Blob([bytes], { type }));
    } catch (err) {
      reject(err);
    }
  });
}

// True when the browser honours <a download>. iOS Safari has historically
// ignored it, which turns a download into a navigation: the tab leaves the
// dashboard and no file is saved. Checked as a capability rather than by sniffing
// the user agent.
export function supportsAnchorDownload(doc = typeof document !== "undefined" ? document : null) {
  if (!doc) return false;
  return "download" in doc.createElement("a");
}

// Hands a Blob to the user — by download where that works, and by opening the
// object URL where it does not, so iOS reaches the share sheet instead of
// silently doing nothing.
export function downloadBlob(blob, filename, {
  doc = typeof document !== "undefined" ? document : null,
  urlApi = typeof URL !== "undefined" ? URL : null,
  win = typeof window !== "undefined" ? window : null,
} = {}) {
  if (!doc || !urlApi) return false;
  const url = urlApi.createObjectURL(blob);
  let delivered = false;

  if (supportsAnchorDownload(doc)) {
    const link = doc.createElement("a");
    link.href = url;
    link.download = filename;
    link.rel = "noopener";
    doc.body.appendChild(link);
    link.click();
    doc.body.removeChild(link);
    delivered = true;
  } else if (win && typeof win.open === "function") {
    delivered = !!win.open(url, "_blank");
  }

  // Revoking inline can cancel an in-flight download in some browsers, so this
  // is deferred: long enough for the browser to have taken its own reference,
  // short enough not to leak the buffer for the life of the session.
  if (typeof setTimeout === "function") {
    setTimeout(() => urlApi.revokeObjectURL(url), 60_000);
  }
  return delivered;
}

// --- PDF text ----------------------------------------------------------------
//
// jsPDF's built-in Helvetica is a WinAnsi font. Characters outside that set are
// not drawn as themselves: the range line of every exported drift report read
// `2026-02-01 !' 2026-08-01`, because "→" has no glyph (#141). Embedding a
// Unicode TTF would fix it in general but costs a few hundred kB in the bundle
// for one arrow, so the export maps the characters our own strings actually
// contain onto ones the font has.
//
// The Montenegrin header needs the same treatment: č, ć and đ are Latin-2, not
// WinAnsi. Transliterating the whole diacritic set — including š and ž, which
// WinAnsi does carry — keeps a word uniform rather than half-accented.
const PDF_CHAR_MAP = {
  "→": "->", "←": "<-", "⇒": "=>", "⚡": "",
  "č": "c", "ć": "c", "đ": "dj", "š": "s", "ž": "z",
  "Č": "C", "Ć": "C", "Đ": "Dj", "Š": "S", "Ž": "Z",
};

// WinAnsi's typographic block sits above U+00FF but is encodable and has glyphs
// in the standard font — verified by rendering each and reading the byte back
// out of the content stream: em dash 0x97, en dash 0x96, ellipsis 0x85, the
// curly quotes 0x91–0x94, bullet 0x95. The header already relies on the em dash.
const PDF_KEEP_ABOVE_LATIN1 = new Set(["–", "—", "…", "‘", "’", "“", "”", "„", "•"]);

// Everything else above U+00FF is dropped rather than substituted: an absent
// character reads as a typo, a wrong glyph reads as corruption.
export function pdfSafeText(value) {
  let out = "";
  for (const ch of String(value ?? "")) {
    if (Object.prototype.hasOwnProperty.call(PDF_CHAR_MAP, ch)) {
      out += PDF_CHAR_MAP[ch];
    } else if (ch.codePointAt(0) <= 0xff || PDF_KEEP_ABOVE_LATIN1.has(ch)) {
      out += ch;
    }
  }
  return out;
}

// --- Drift report header ------------------------------------------------------
//
// Built here, and pure, because the wording is the substance of #141 rather than
// a detail of it: the PDF outlives the session that produced it and its reader
// cannot ask a follow-up question, so every number in the header has to carry
// what it is conditional on.

// `alertCount` is points containing at least one reading past the threshold, not
// separate events — 300 of 300 points is a real result, and "Alerts: 300" read
// as 300 incidents.
export function driftHeadlineLine({ alertCount, pointCount, peakDrift, threshold, t }) {
  return [
    t("exportAlertPoints", { n: alertCount, total: pointCount }),
    t("exportPeakDrift", { v: peakDrift }),
    t("exportThreshold", { v: threshold }),
  ].join("   |   ");
}

// Drift is computed from the spike-filtered series, so the peak in the line
// above is conditional on that filtering. A reader holding only the PDF has no
// other way to learn it. The zero case states its own result rather than
// omitting the line, which would be indistinguishable from never having checked.
export function driftSpikeLines({ spikeCount, t }) {
  const n = Number(spikeCount) || 0;
  if (n <= 0) return [t("exportSpikeNone")];
  return [
    n === 1 ? t("exportSpikeOne") : t("exportSpikeMany", { n }),
    t("exportSpikeCaveat"),
  ];
}
